import React, { MouseEventHandler, useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import Caret from 'components/Common/Caret'
import Clickable from 'components/Common/Clickable'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { pluralizeToString } from 'lib/string/pluralize'
import { rem } from 'polished'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { generateDurations } from 'selectors/offerPage/bedbankOfferPageSelectors'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import useHasMounted from 'hooks/useHasMounted'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

const DurationFromText = styled(BodyText)`
  transition: color 0.2s;
`

const DurationCaret = styled(Caret)`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateY(100%) translateX(-50%);
  opacity: 0;
  transition: opactiy 0.2s, border-color 0.2s;
  border-top-color: ${props => props.theme.palette.brand.primary.normal};
  border-width: ${rem(8)};

  &.visible {
    opacity: 1;
  }
`

const DurationOption = styled(Clickable)`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: color 0.2s, background-color 0.2s;
  box-shadow: ${props => props.theme.shadow.bottom.small};
  position: relative;
  flex-shrink: 0;
  min-width: ${rem(120)};
  align-self: flex-end;

  &.selected {
    color: ${props => props.theme.palette.neutral.default.eight};
    background-color: ${props => props.theme.palette.brand.primary.normal};

    ${mediaHoverable} {
      &:hover {
        color: ${props => props.theme.palette.neutral.default.eight};
        background-color: ${props => props.theme.palette.brand.primary.hover};

        ${DurationCaret} {
          border-top-color: ${props => props.theme.palette.brand.primary.hover};
        }
      }
    }
    &:focus {
      color: ${props => props.theme.palette.neutral.default.eight};
      background-color: ${props => props.theme.palette.brand.primary.hover};

      ${DurationCaret} {
        border-top-color: ${props => props.theme.palette.brand.primary.hover};
      }
    }
  }

  &:hover, &:focus {
    color: ${props => props.theme.palette.neutral.default.eight};
    background-color: ${props => props.theme.palette.brand.primary.normal};

    ${DurationFromText} {
      color: ${props => props.theme.palette.neutral.default.eight};
    }
  }
`

const DurationOptionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${rem(12)};
`

const PromoTag = styled(Caption)`
  padding: 0 ${rem(4)};
  background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
`

const OfferDurations = styled.div`
  ${mediaQueryUp.desktop} {
    &.centered {
      display: flex;
      justify-content: center;
    }
  }
`

interface Props {
  checkIn?: string
  checkOut?: string
  selectedDuration: number
  durationOptions: Array<App.BedbankDurationOption>
  className?: string
}

function BedbankDurationTabs(props: Props) {
  const { className, selectedDuration, checkIn, checkOut, durationOptions } = props

  const hasMounted = useHasMounted()
  const isMobileScreen = useScreenSizeOnly('mobile')
  const carouselRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<Record<string, HTMLButtonElement>>({})
  const offerPageDispatch = useOfferPageDispatch()

  const onDurationClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const duration = parseInt(event.currentTarget.dataset.duration!, 10)

    if (checkIn && checkOut) {
      const newCheckout = moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT)
      offerPageDispatch({ type: OfferPageActions.setCheckOutDate, checkOut: newCheckout })
    }

    offerPageDispatch({ type: OfferPageActions.setDuration, duration })
  }, [offerPageDispatch, checkIn, checkOut])

  useEffect(() => {
    const tabElement = tabRefs.current[selectedDuration]
    // ensure the selected duration is always visible in the carousel
    if (tabElement) {
      const startElementPx = tabElement.offsetLeft
      const endElementPx = tabElement.offsetLeft + tabElement.offsetWidth
      const startParentPx = tabElement.parentElement?.scrollLeft ?? 0
      const endParentPx = (tabElement.parentElement?.offsetWidth ?? 0) + (tabElement.parentElement?.scrollLeft ?? 0)

      // check if our elements bounds are outside of the currently visible bounds on the scroll parent
      const isNotVisible = startElementPx > endParentPx || endElementPx < startParentPx
      if (isNotVisible && tabElement.parentElement) {
        tabElement.parentElement.scrollLeft = startElementPx
      }
    }
  }, [selectedDuration])

  useEffect(() => {
    if (!hasMounted) {
      return
    }

    const selectedOption = durationOptions.find(option => option.duration === selectedDuration)

    // Ensure promo 'SPECIAL' tags are prominent when a bedbank has promotions
    if (selectedOption) {
      const anchoringTab = isMobileScreen ? tabRefs.current[selectedOption.duration] : tabRefs.current[selectedOption.duration - 2]
      carouselRef.current?.scrollTo({
        left: anchoringTab?.offsetLeft,
        behavior: 'smooth',
      })

      if (checkIn && checkOut && !selectedOption.hasPromo) {
        carouselRef.current?.scrollTo({
          left: tabRefs.current[selectedOption.duration]?.offsetLeft,
          behavior: 'smooth',
        })
      }
    } else {
      const firstPromoOption = durationOptions.find(option => option.hasPromo)
      if (firstPromoOption) {
        const anchoringTab = isMobileScreen ? tabRefs.current[firstPromoOption.duration] : tabRefs.current[firstPromoOption.duration - 2]
        carouselRef.current?.scrollTo({
          left: anchoringTab?.offsetLeft,
          behavior: 'smooth',
        })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMounted])

  return (
    <OfferDurations className={clsx(className, { centered: durationOptions.length < 8 })}>
      <CardCarousel ref={carouselRef} gap={12} pageSize={Math.min(durationOptions.length, 8)}>
        {durationOptions.map((option, index) => {
          const selected = option.duration === selectedDuration
          const hasPromo = option.hasPromo

          return <DurationOption
            key={index}
            className={clsx({ selected })}
            data-testid={`nights-${option.duration}`}
            data-duration={option.duration}
            onClick={onDurationClick}
            ref={(ref: HTMLButtonElement) => {
              if (ref) {
                tabRefs.current[option.duration] = ref
              }
            }}
          >
            {hasPromo && <PromoTag
              align="center"
              colour="neutral-one"
              weight="bold"
              variant="medium"
              wrap="no-wrap"
            >
              SPECIAL
            </PromoTag>}
            <DurationOptionContent>
              <BodyText variant="medium" weight="bold" wrap="no-wrap">{pluralizeToString('night', option.duration)}</BodyText>
              <DurationCaret className={clsx({ visible: selected })} />
            </DurationOptionContent>
          </DurationOption>
        })}
      </CardCarousel>
    </OfferDurations>

  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  selectedDuration: (state.checkInDate && state.checkOutDate) ? moment(state.checkOutDate).diff(moment(state.checkInDate), 'days') : state.duration,
  durationOptions: generateDurations(state, appState),
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  offer: state.bedbankOffer,
}), BedbankDurationTabs)
